import { ElnLinkRenderer } from "@digitallab/grid-common-components";
import { DateTimeDetailValue } from "../../../../components/shared/details-cells/DateTimeDetailValue";
import DetailsCells from "../../../../components/shared/details-cells/DetailsCells";
import COMMON_LOGS_DATA_MODEL from "../../../../utils/constants/commonLogsDataModel";
import { DESCRIPTION_FIELD } from "../../../../utils/constants/descriptionField";
import RunStatusLabel from "../../common-for-log-sheet-and-run-logs/RunStatusLabel";
import {
  ApproverUserName,
  Flex,
  RunLogWrapper,
  CustomDetailsCellStyled,
  MediaTypeCell,
  GxpReadyCell,
  SystemStatusCell,
  CreatedAtCell,
  CreatedTimeAtCell,
  GxpRelevantCell
} from "../../common-for-log-sheet-and-run-logs/table-common-meta-info";
import ValueOrFallbackText from "../../common-for-log-sheet-and-run-logs/ValueOrFallbackText";
import { formatUtcToLocal } from "../../helpers";
import { getDateTimeDisplay, getDateTimeDetailProps } from "../../../../utils/helpers/text";
import RUN_LOGS_DATA_MODEL from "./../../../../utils/constants/runLogsDataModel";

const createTimeFieldComponent = (utcDateTimeField, legacyTimeField) => ({ item }) => {
  const utcDateTime = item?.[utcDateTimeField];
  if (utcDateTime) {
    return (
      <DateTimeDetailValue
        time={formatUtcToLocal(utcDateTime, { variant: 'time' })}
        skipTimeParse={true}
      />
    );
  }
  return <DateTimeDetailValue time={item?.[legacyTimeField]} />;
};

export const RunLogTableMeta = {
  expandable: true,
  ExpandedComponent: ({ item, infoMeta }) => (
    <Flex>
      <DetailsCells Wrapper={RunLogWrapper} item={item} infoMeta={infoMeta} CellWrapper={CustomDetailsCellStyled} />
    </Flex>
  ),
  // expanded element from chevron click for main run log table view
  expandedComponentProps: {
    infoMeta: {
      fields: [
        {
          label: RUN_LOGS_DATA_MODEL.runStartTime.value,
          component: createTimeFieldComponent('runStartDateTime', 'runStartTime')
        },
        {
          label: RUN_LOGS_DATA_MODEL.runEndTime.value,
          component: createTimeFieldComponent('runEndDateTime', 'runEndTime')
        },
        {
          label: COMMON_LOGS_DATA_MODEL.createdAtTime.value,
          component: CreatedTimeAtCell
        },
        {
          label: COMMON_LOGS_DATA_MODEL.operatorUserName.value,
          field: ["operatorUserName"]
        },
        {
          label: RUN_LOGS_DATA_MODEL.runIdentification.value,
          field: ["runIdentification"]
        },
        {
          label: RUN_LOGS_DATA_MODEL.eLNid.value,
          component: ({ item }) => <ElnLinkRenderer elnExperiment={item.eLNid} />
        },
        {
          label: RUN_LOGS_DATA_MODEL.samplesProcessed.value,
          field: ["samplesProcessed"]
        },
        {
          label: RUN_LOGS_DATA_MODEL.numberOfRuns.value,
          field: ["numberOfRuns"]
        },
        {
          label: RUN_LOGS_DATA_MODEL.defectId.value,
          field: ["defectId"]
        },
        {
          label: RUN_LOGS_DATA_MODEL.mediaType.value,
          component: MediaTypeCell
        },
        {
          label: RUN_LOGS_DATA_MODEL.assay.value,
          component: ({ item }) => <span>{item.assay && item.assay.length > 0 ? item.assay.join(", ") : "-"}</span>,
          props: {
            expand: true
          }
        },
        DESCRIPTION_FIELD
      ]
    }
  },
  fields: {
    detailExpander: {
      text: ""
    },
    runStartDate: {
      sortable: true,
      text: RUN_LOGS_DATA_MODEL.runStartDate.value,
      component: ({ item }) => getDateTimeDisplay(item, 'runStartDateTime', 'runStartDate')
    },
    runEndDate: {
      sortable: true,
      text: RUN_LOGS_DATA_MODEL.runEndDate.value,
      component: ({ item }) => (
        <DateTimeDetailValue {...getDateTimeDetailProps(item, 'runEndDateTime', 'runEndDate', 'runEndTime')} />
      )
    },
    createdAt: {
      sortable: true,
      text: COMMON_LOGS_DATA_MODEL.createdAt.value,
      component: CreatedAtCell
    },
    equipmentModel: {
      text: COMMON_LOGS_DATA_MODEL.equipmentModel.value
    },
    serialNumber: {
      text: COMMON_LOGS_DATA_MODEL.serialNumber.value
    },
    equipmentId: {
      text: COMMON_LOGS_DATA_MODEL.equipmentId.value
    },
    equipmentNickName: {
      text: COMMON_LOGS_DATA_MODEL.equipmentNickName.value
    },
    runStatus: {
      text: RUN_LOGS_DATA_MODEL.runStatus.value,
      component: RunStatusLabel
    },
    gxpRelevantRun: {
      text: RUN_LOGS_DATA_MODEL.gxpRelevantRun.value
    },
    ...ApproverUserName,
    gxpRelevant: {
      text: COMMON_LOGS_DATA_MODEL.gxpRelevant.value,
      component: GxpRelevantCell
    },
    gxpReady: {
      text: RUN_LOGS_DATA_MODEL.gxpReady.value,
      component: GxpReadyCell
    },
    systemStatus: {
      text: RUN_LOGS_DATA_MODEL.systemStatus.value,
      component: SystemStatusCell
    },
    operatorUserName: {
      text: COMMON_LOGS_DATA_MODEL.operatorUserName.value
    },
    runIdentification: {
      text: RUN_LOGS_DATA_MODEL.runIdentification.value
    },
    eLNid: {
      text: RUN_LOGS_DATA_MODEL.eLNid.value,
      component: ({ item }) => <ElnLinkRenderer elnExperiment={item.eLNid} />
    },
    samplesProcessed: {
      text: RUN_LOGS_DATA_MODEL.samplesProcessed.value
    },
    numberOfRuns: {
      text: RUN_LOGS_DATA_MODEL.numberOfRuns.value
    },
    defectId: {
      text: RUN_LOGS_DATA_MODEL.defectId.value
    },
    tipsUsed: {
      text: RUN_LOGS_DATA_MODEL.tipsUsed.value,
      component: ({ item }) => (
        <ValueOrFallbackText reduxPath={["runLogsForm", "tips"]} item={item.tipsUsed} defaultValue="-" />
      )
    },
    mediaType: {
      text: RUN_LOGS_DATA_MODEL.mediaType.value,
      component: MediaTypeCell
    },
    assay: {
      text: RUN_LOGS_DATA_MODEL.assay.value,
      component: ({ item }) => <span>{item.assay && item.assay.length > 0 ? item.assay.join(", ") : "-"}</span>
    },
    description: {
      text: RUN_LOGS_DATA_MODEL.description.value
    }
  }
};

//Run Log meta for centric view
export const RunLogTableEquipmentCentricMeta = {
  expandable: true,
  ExpandedComponent: ({ item, infoMeta }) => (
    <Flex>
      <DetailsCells Wrapper={RunLogWrapper} item={item} infoMeta={infoMeta} CellWrapper={CustomDetailsCellStyled} />
    </Flex>
  ),
  // expanded element from chevron click for centric view
  expandedComponentProps: {
    infoMeta: {
      fields: [
        {
          label: RUN_LOGS_DATA_MODEL.runStartTime.value,
          component: createTimeFieldComponent('runStartDateTime', 'runStartTime')
        },
        {
          label: RUN_LOGS_DATA_MODEL.runEndTime.value,
          component: createTimeFieldComponent('runEndDateTime', 'runEndTime')
        },
        {
          label: COMMON_LOGS_DATA_MODEL.createdAtTime.value,
          component: CreatedTimeAtCell
        },
        {
          label: COMMON_LOGS_DATA_MODEL.operatorUserName.value,
          field: ["operatorUserName"]
        },
        {
          label: RUN_LOGS_DATA_MODEL.runIdentification.value,
          field: ["runIdentification"]
        },
        {
          label: RUN_LOGS_DATA_MODEL.eLNid.value,
          component: ({ item }) => <ElnLinkRenderer elnExperiment={item.eLNid} />
        },
        {
          label: RUN_LOGS_DATA_MODEL.samplesProcessed.value,
          field: ["samplesProcessed"]
        },
        {
          label: RUN_LOGS_DATA_MODEL.numberOfRuns.value,
          field: ["numberOfRuns"]
        },
        {
          label: RUN_LOGS_DATA_MODEL.defectId.value,
          field: ["defectId"]
        },
        {
          label: RUN_LOGS_DATA_MODEL.mediaType.value,
          component: MediaTypeCell
        },
        {
          label: RUN_LOGS_DATA_MODEL.assay.value,
          component: ({ item }) => <span>{item.assay && item.assay.length > 0 ? item.assay.join(", ") : "-"}</span>,
          props: {
            expand: true
          }
        },
        DESCRIPTION_FIELD
      ]
    }
  },
  fields: {
    detailExpander: {
      text: ""
    },
    runStartDate: {
      sortable: true,
      text: RUN_LOGS_DATA_MODEL.runStartDate.value,
      component: ({ item }) => getDateTimeDisplay(item, 'runStartDateTime', 'runStartDate')
    },
    runEndDate: {
      sortable: true,
      text: RUN_LOGS_DATA_MODEL.runEndDate.value,
      component: ({ item }) => (
        <DateTimeDetailValue {...getDateTimeDetailProps(item, 'runEndDateTime', 'runEndDate', 'runEndTime')} />
      )
    },
    createdAt: {
      sortable: true,
      text: COMMON_LOGS_DATA_MODEL.createdAt.value,
      component: CreatedAtCell
    },
    runStatus: {
      text: RUN_LOGS_DATA_MODEL.runStatus.value,
      component: RunStatusLabel
    },
    gxpRelevantRun: {
      text: RUN_LOGS_DATA_MODEL.gxpRelevantRun.value
    },
    ...ApproverUserName,
    gxpRelevant: {
      text: COMMON_LOGS_DATA_MODEL.gxpRelevant.value,
      component: GxpRelevantCell
    },
    gxpReady: {
      text: RUN_LOGS_DATA_MODEL.gxpReady.value,
      component: GxpReadyCell
    },
    systemStatus: {
      text: RUN_LOGS_DATA_MODEL.systemStatus.value,
      component: SystemStatusCell
    },
    operatorUserName: {
      text: COMMON_LOGS_DATA_MODEL.operatorUserName.value
    },
    runIdentification: {
      text: RUN_LOGS_DATA_MODEL.runIdentification.value
    },
    eLNid: {
      text: RUN_LOGS_DATA_MODEL.eLNid.value,
      component: ({ item }) => <ElnLinkRenderer elnExperiment={item.eLNid} />
    },
    samplesProcessed: {
      text: RUN_LOGS_DATA_MODEL.samplesProcessed.value
    },
    numberOfRuns: {
      text: RUN_LOGS_DATA_MODEL.numberOfRuns.value
    },
    defectId: {
      text: RUN_LOGS_DATA_MODEL.defectId.value
    },
    tipsUsed: {
      text: RUN_LOGS_DATA_MODEL.tipsUsed.value,
      component: ({ item }) => (
        <ValueOrFallbackText reduxPath={["runLogsForm", "tips"]} item={item.tipsUsed} defaultValue="-" />
      )
    },
    mediaType: {
      text: RUN_LOGS_DATA_MODEL.mediaType.value,
      component: MediaTypeCell
    },
    assay: {
      text: RUN_LOGS_DATA_MODEL.assay.value,
      component: ({ item }) => <span>{item.assay && item.assay.length > 0 ? item.assay.join(", ") : "-"}</span>
    },
    description: {
      text: RUN_LOGS_DATA_MODEL.description.value
    }
  }
};

export default RunLogTableMeta;
